import React, { useState } from 'react';
import { authAPI } from '../../services/api';

function ChangePasswordModal({ isOpen, onClose, user }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Forgot password flow state
  const [mode, setMode] = useState('change'); // 'change' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);

  const resetState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setError('');
    setSuccess('');
    setMode('change');
    setForgotEmail('');
    setForgotSent(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('Current password is required.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setIsSubmitting(true);

    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully.');
      setTimeout(handleClose, 1500);
    } catch (err) {
      const message = err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.message
        || 'Failed to change password. Please check your current password.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!forgotEmail) {
      setError('Email address is required.');
      return;
    }

    setIsForgotSubmitting(true);

    try {
      // API triggers a password reset email to the user. The response
      // is intentionally generic (same for known/unknown emails) to
      // prevent enumeration. The user must check their email for the
      // reset link and complete the flow on the /reset-password page.
      await authAPI.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      // Even on error, show the same generic message — never tell
      // the caller whether the email is in our system.
      setForgotSent(true);
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal change-password-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {mode === 'forgot' ? 'Reset Forgotten Password' : 'Change Password'}
          </h2>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          {mode === 'forgot' ? (
            <>
              {!forgotSent ? (
                <>
                  <p className="forgot-description">
                    Enter the email address associated with your account.
                    A new temporary password will be generated and sent to you.
                  </p>
                  <form onSubmit={handleForgotPassword}>
                    <div className="form-group">
                      <label htmlFor="forgot-email">Email Address</label>
                      <input
                        type="email"
                        id="forgot-email"
                        className="form-control"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoFocus
                      />
                    </div>
                    {error && <div className="form-error">{error}</div>}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => { setMode('change'); setError(''); }}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isForgotSubmitting}
                      >
                        {isForgotSubmitting ? 'Sending...' : 'Send Reset Email'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="forgot-success">
                    <div className="success-icon">&#10003;</div>
                    <p>If that email address is in our system, a password reset link has been sent.</p>
                    <p className="forgot-success-hint">
                      Check your email and follow the link to set a new password.
                    </p>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {success ? (
                <div className="change-success">
                  <div className="success-icon">&#10003;</div>
                  <p>{success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="current-password">Current Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        id="current-password"
                        className="form-control"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        tabIndex={-1}
                      >
                        {showCurrentPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div className="form-group forgot-link-wrapper">
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => { setMode('forgot'); setError(''); }}
                    >
                      I forgot
                    </button>
                  </div>

                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="new-password"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        tabIndex={-1}
                      >
                        {showNewPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <span className="form-hint">Minimum 8 characters</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm New Password</label>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="confirm-password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  {error && <div className="form-error">{error}</div>}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
